import type { Theme } from '@mend/mui';
import type { BoxProps } from '@mui/material/Box';
import type { TypographyProps } from '@mui/material/Typography';
import type { SxProps } from '@mui/system';
import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

interface InfoSectionProps {
  title: string;
  children: ReactNode;
  indented?: boolean;
  titleProps?: Omit<TypographyProps, 'variant' | 'children'>;
  // Allow full sx flexibility (object, array, or function)
  sx?: SxProps<Theme>;
}

export function InfoSection({
  title,
  children,
  indented = false,
  titleProps,
  sx,
}: InfoSectionProps) {
  const titleSx: SxProps<Theme> = [indented ? { pl: 5 } : null, sx, titleProps?.sx].filter(
    Boolean
  ) as SxProps<Theme>;
  return (
    <>
      <Typography variant="subtitle1" {...titleProps} sx={titleSx}>
        {title}
      </Typography>
      {children}
    </>
  );
}

interface InfoListProps {
  items: ReactNode[];
  nested?: boolean;
  /** The list element type to render (unordered by default) */
  component?: 'ul' | 'ol';
  boxProps?: Omit<BoxProps, 'component' | 'children'>;
  // When Typography renders as an <li>, its ref type changes. Tie the props to that shape.
  itemProps?: Omit<TypographyProps<'li'>, 'component' | 'variant' | 'children'>;
}

export function InfoList({ items, component = 'ul', boxProps, itemProps }: InfoListProps) {
  return (
    <Box component={component} {...boxProps}>
      {items.map((item, index) => (
        <Typography key={index} component="li" variant="body2" {...itemProps}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

interface InfoParagraphProps extends Omit<TypographyProps, 'variant' | 'paragraph'> {
  children: ReactNode;
}

export function InfoParagraph({ children, ...props }: InfoParagraphProps) {
  return (
    <Typography variant="body2" paragraph {...props}>
      {children}
    </Typography>
  );
}

interface InfoEmphasisProps {
  children: ReactNode;
}

export function InfoEmphasis({ children }: InfoEmphasisProps) {
  return (
    <Typography component="span" variant="inherit" fontWeight="bold">
      {children}
    </Typography>
  );
}

interface InfoCalculationProps {
  formula: string;
  steps: ReactNode[];
  indented?: boolean;
}

export function InfoCalculation({ formula, steps, indented = true }: InfoCalculationProps) {
  return (
    <>
      <Typography variant="subtitle1" sx={indented ? { pl: 5 } : undefined}>
        {formula}
      </Typography>
      <InfoList items={steps} />
    </>
  );
}

interface InfoTemplateProps extends BoxProps {
  children: ReactNode;
}

export function InfoTemplate({ children, ...boxProps }: InfoTemplateProps) {
  return <Box {...boxProps}>{children}</Box>;
}
